import { AppConfig } from '@/types';

// Environment configuration
const getConfig = (): AppConfig => {
  const region = import.meta.env.VITE_AWS_REGION || '';
  const userPoolId = import.meta.env.VITE_COGNITO_USER_POOL_ID || '';
  const userPoolClientId = import.meta.env.VITE_COGNITO_USER_POOL_CLIENT_ID || '';
  const identityPoolId = import.meta.env.VITE_COGNITO_IDENTITY_POOL_ID || '';
  const cognitoDomain = import.meta.env.VITE_COGNITO_DOMAIN || '';
  const fallbackOrigin = import.meta.env.VITE_FALLBACK_ORIGIN || '';
  // OIDC provider name (e.g., "azuread" for Azure AD OIDC)
  // This should match the provider_name in the backend Terraform oidc_provider_name variable
  const cognitoOidcProvider = import.meta.env.VITE_COGNITO_OIDC_PROVIDER || import.meta.env.VITE_COGNITO_IDP_PROVIDER || '';
  // Legacy SAML support (deprecated in favor of OIDC)
  const cognitoSamlProvider = import.meta.env.VITE_COGNITO_SAML_PROVIDER || '';
  
  // OAuth redirect URLs
  // CRITICAL: These MUST match the current origin where the app is running
  // Amplify's InvalidOriginException occurs when redirect URLs don't match the current origin
  // Priority (must match current origin):
  // 1. Use current origin (window.location.origin) - ALWAYS at runtime
  // 2. Fall back to explicit environment variables ONLY if they match current origin
  // 3. Never use CloudFront URL if app is running on a different origin
  const getCurrentOrigin = () => {
    // ALWAYS use current origin at runtime - this is what Amplify validates
    if (typeof window !== 'undefined' && window.location) {
      return window.location.origin;
    }
    
    // Fallback for SSR or build time (shouldn't be used for OAuth)
    return fallbackOrigin;
  };
  
  const currentOrigin = getCurrentOrigin();
  
  // Build redirect URLs using current origin
  // If explicit URLs are set via env vars, validate they match current origin
  let redirectSignIn: string;
  let redirectSignOut: string;
  
  const explicitRedirectSignIn = import.meta.env.VITE_COGNITO_REDIRECT_SIGN_IN;
  const explicitRedirectSignOut = import.meta.env.VITE_COGNITO_REDIRECT_SIGN_OUT;
  
  if (explicitRedirectSignIn) {
    try {
      const explicitUrl = new URL(explicitRedirectSignIn);
      const explicitOrigin = explicitUrl.origin;
      
      // Validate that explicit redirect URL matches current origin
      // If they don't match, use current origin instead (with warning)
      if (explicitOrigin === currentOrigin) {
        redirectSignIn = explicitRedirectSignIn;
      } else {
        console.warn(`OAuth Redirect URL mismatch detected. Explicit URL origin (${explicitOrigin}) doesn't match current origin (${currentOrigin}). Using current origin instead.`);
        redirectSignIn = `${currentOrigin}/oauth2/callback`;
      }
    } catch {
      // Invalid URL format, use current origin
      redirectSignIn = `${currentOrigin}/oauth2/callback`;
    }
  } else {
    // No explicit URL set, use current origin
    redirectSignIn = `${currentOrigin}/oauth2/callback`;
  }
  
  if (explicitRedirectSignOut) {
    try {
      const explicitUrl = new URL(explicitRedirectSignOut);
      const explicitOrigin = explicitUrl.origin;
      
      // Validate that explicit redirect URL matches current origin
      if (explicitOrigin === currentOrigin) {
        redirectSignOut = explicitRedirectSignOut;
      } else {
        console.warn(`OAuth Redirect URL mismatch detected. Explicit URL origin (${explicitOrigin}) doesn't match current origin (${currentOrigin}). Using current origin instead.`);
        redirectSignOut = `${currentOrigin}/login`;
      }
    } catch {
      // Invalid URL format, use current origin
      redirectSignOut = `${currentOrigin}/login`;
    }
  } else {
    // No explicit URL set, use current origin
    redirectSignOut = `${currentOrigin}/login`;
  }
  
  // Log redirect URLs for debugging
  // Always log to help diagnose configuration issues
  if (import.meta.env.DEV || import.meta.env.VITE_DEBUG_CONFIG || import.meta.env.MODE === 'production') {
    console.log('OAuth Redirect URLs Configuration:', {
      redirectSignIn,
      redirectSignOut,
      currentOrigin,
      windowLocationOrigin: typeof window !== 'undefined' ? window.location.origin : 'N/A',
      explicitRedirectSignIn,
      explicitRedirectSignOut,
      cloudFrontUrl: import.meta.env.VITE_CLOUDFRONT_URL,
      environment: import.meta.env.MODE,
      isDev: import.meta.env.DEV,
      note: 'Redirect URLs are built using current origin to avoid InvalidOriginException',
    });
  }
  
  const apiGatewayUrl = import.meta.env.VITE_API_GATEWAY_URL || '';
  const vendorApiGatewayUrl = import.meta.env.VITE_VENDOR_API_GATEWAY_URL || '';
  const s3Bucket = import.meta.env.VITE_S3_BUCKET || '';
  const cloudFrontUrl = import.meta.env.VITE_CLOUDFRONT_URL || '';

  return {
    region,
    userPoolId,
    userPoolClientId,
    identityPoolId,
    cognitoDomain,
    cognitoOidcProvider,
    cognitoSamlProvider, // Legacy support
    cognitoIdpProvider: cognitoOidcProvider, // Alias for backwards compatibility
    redirectSignIn,
    redirectSignOut,
    apiGatewayUrl,
    vendorApiGatewayUrl,
    s3Bucket,
    cloudFrontUrl,
  };
};

export const config = getConfig();

const AWS_ENVIRONMENTS = new Set(['dev', 'stg', 'prod']);

function awsEnvironmentFromGatewayUrl(url: string): string | null {
  try {
    const stage = new URL(url).pathname.split('/').filter(Boolean)[0]?.toLowerCase();
    if (stage && AWS_ENVIRONMENTS.has(stage)) {
      return stage;
    }
  } catch {
    return null;
  }
  return null;
}

/**
 * Display-only vendor bucket prefix (addonBefore). Backend still enforces the real prefix.
 * Prefer VITE_VENDOR_BUCKET_PREFIX; else derive env from the API Gateway stage path.
 */
export function vendorBucketPrefix(): string {
  const explicit = (import.meta.env.VITE_VENDOR_BUCKET_PREFIX || '').trim();
  if (explicit) {
    return explicit.endsWith('-') ? explicit : `${explicit}-`;
  }
  const envName = awsEnvironmentFromGatewayUrl(
    config.vendorApiGatewayUrl || config.apiGatewayUrl || ''
  );
  if (envName) {
    return `data-exchange-${envName}-`;
  }
  return 'data-exchange-…-';
}

// Validate configuration and warn if incomplete
if (!config.userPoolId || !config.userPoolClientId) {
  console.warn('AWS Amplify configuration is incomplete. Some features may not work correctly.');
  console.warn('Please ensure .env.local is configured with valid AWS credentials.');
}

// AWS Amplify configuration
// Build Auth config with OAuth if domain is available
// IMPORTANT: In AWS Amplify v6, OAuth config must be under Auth.Cognito.loginWith.oauth
// CRITICAL: OAuth config with redirect URLs must be built at runtime (not at module load)
// to ensure redirect URLs always match the current origin and avoid InvalidOriginException
const buildAuthConfig = (useRuntimeOrigin: boolean = false) => {
  const cognitoConfig: any = {
    userPoolId: config.userPoolId,
    userPoolClientId: config.userPoolClientId,
    identityPoolId: config.identityPoolId,
    loginWith: {
      email: true,
      username: true,
    },
  };
  
  // Add OAuth configuration under loginWith if domain is available
  // OAuth must be nested under Cognito.loginWith, not at Auth level
  if (config.cognitoDomain) {
    // CRITICAL: Always use runtime origin for redirect URLs to avoid InvalidOriginException
    // InvalidOriginException occurs when redirect URLs don't match the current origin
    // This is a security feature - Amplify requires redirect URLs to match where the app is running
    let redirectSignInUrl: string;
    let redirectSignOutUrl: string;
    
    if (useRuntimeOrigin && typeof window !== 'undefined' && window.location) {
      // Use current origin at runtime (when function is called dynamically)
      const currentOrigin = window.location.origin;
      redirectSignInUrl = `${currentOrigin}/oauth2/callback`;
      redirectSignOutUrl = `${currentOrigin}/login`;
    } else {
      // Use config values (built using current origin logic in getConfig)
      // These should already match current origin due to the logic in getConfig()
      redirectSignInUrl = config.redirectSignIn;
      redirectSignOutUrl = config.redirectSignOut;
    }
    
    cognitoConfig.loginWith.oauth = {
      domain: config.cognitoDomain,
      scopes: ['email', 'openid', 'profile'],
      redirectSignIn: [redirectSignInUrl],
      redirectSignOut: [redirectSignOutUrl],
      responseType: 'code' as const,
    };
  }
  
  return {
    Cognito: cognitoConfig,
  };
};

// Initial config (built at module load time)
// NOTE: This may have incorrect redirect URLs if built before window.location is available
// The authStore will rebuild this with correct URLs before using it
export const amplifyConfig = {
  Auth: buildAuthConfig(false), // Don't use runtime origin at module load
  Storage: {
    S3: {
      bucket: config.s3Bucket,
      region: config.region,
    },
  },
  API: {
    REST: {
      'data-exchange-api': {
        endpoint: config.apiGatewayUrl,
        region: config.region,
      },
    },
  },
};

// Export function to rebuild config with current origin
// This should be called before using OAuth (e.g., in authStore before signInWithRedirect)
export const rebuildAmplifyConfigWithCurrentOrigin = () => {
  const newAuthConfig = buildAuthConfig(true); // Use runtime origin
  return {
    ...amplifyConfig,
    Auth: newAuthConfig,
  };
};

// Debug: Log OAuth configuration (always log in development, or if explicitly enabled)
if (import.meta.env.DEV || import.meta.env.VITE_DEBUG_CONFIG) {
  if (config.cognitoDomain) {
    const cognitoConfig: any = amplifyConfig.Auth.Cognito;
    const oauthConfig = cognitoConfig?.loginWith?.oauth;
    console.log('OAuth configuration built:', {
      domain: config.cognitoDomain,
      redirectSignIn: config.redirectSignIn,
      redirectSignOut: config.redirectSignOut,
      hasOAuthConfig: !!oauthConfig,
      oauthConfigLocation: 'Auth.Cognito.loginWith.oauth',
      oauthConfig: oauthConfig,
      environment: import.meta.env.MODE,
      isDev: import.meta.env.DEV,
      cloudFrontUrl: import.meta.env.VITE_CLOUDFRONT_URL,
    });
  } else {
    console.warn('OAuth configuration NOT built - cognitoDomain is missing');
    console.warn('VITE_COGNITO_DOMAIN:', import.meta.env.VITE_COGNITO_DOMAIN);
    console.warn('config.cognitoDomain:', config.cognitoDomain);
  }
}

// UI configuration
export const uiConfig = {
  theme: {
    primaryColor: '#1890ff',
    successColor: '#52c41a',
    warningColor: '#faad14',
    errorColor: '#ff4d4f',
  },
  layout: {
    headerHeight: 64,
    sidebarWidth: 240,
    collapsedSidebarWidth: 80,
  },
  pagination: {
    defaultPageSize: 20,
    pageSizeOptions: ['10', '20', '50', '100'],
  },
};

export default config;
